import type { ReactNode } from 'react';
import iconLogo from './assets/cca_icon_RGB.png';

/** The circular CCA icon mark in every portal banner links here, opening a new tab. */
export const CCA_HOME_URL = 'https://www.confessinganglicans.nz';

interface PortalHeaderProps {
  /** e.g. "Finance Portal", "Parish Portal", "Safe Ministry" */
  portalName: string;
  /** Secondary label shown after the portal name — tool or section name */
  toolName?: string;
  /** User controls (avatar button, email + sign-out, etc.) */
  rightSlot?: ReactNode;
  /** Tab bar or context strip rendered below the brand strip */
  subNav?: ReactNode;
}

/**
 * Org-standard top-of-page header for ALL CCAANZ portals (Steve, 2026-06-13):
 * brand-coloured strip, white circular icon mark, divider, "CCAANZ" eyebrow +
 * portal name. The Events Dashboard header (cca-ticketing forms Layout.tsx) is
 * the reference rendering — keep the two in step.
 *
 * The band is `--color-strip` where a portal sets it, else the design-system
 * `--color-structural` role token, else the legacy `--brand` var (so portals not
 * yet on @cca/ui/styles render unchanged). `--color-strip` exists for a night
 * theme (cca-x53l): Finance and Quota lighten `--color-structural` at night so
 * terracotta TEXT reads on the dark page, and set `--color-strip` in their own
 * `:root` so the band keeps its signature colour in every theme. No shared file
 * declares it — a root-level default would freeze every scoped band.
 *
 * Everything ON the band — the eyebrow, the portal and tool names, the divider —
 * reads `--color-on-strip` (white, tokens.css, the same in every theme: the icon
 * mark is a white-filtered image), never `--color-on-primary`, which a night
 * theme turns dark for its own light primary fill. The CCAANZ eyebrow is SOLID,
 * never a translucent white: on the lightest strip (`--brand-parishes` #86754d)
 * even pure white measures only 4.50:1, so any alpha below 1.0 fails WCAG 2.2
 * SC 1.4.3 AA. Its lower emphasis comes from size, weight and caps, not from
 * transparency — and the same holds for the tool name after the `·`, which
 * inherits the portal name's white and is set apart by weight alone (cca-5weu).
 * Sizes use the `--text-*` scale, each with the token's own value as a fallback
 * because five of the portals wearing this header do not import @cca/ui/styles.
 *
 * Narrow screens (WCAG 2.2 SC 1.4.10 Reflow, cca-69gs): the row is an intrinsic
 * "cluster" — it wraps, with NO breakpoint and NO JS. The brand group (icon +
 * identity, never split) takes the line; when the controls no longer fit beside
 * it they drop onto their own line, pushed RIGHT by `marginInlineStart: auto`,
 * so a right-anchored menu (Sign out, the parish list) still opens on screen.
 * The slot may itself wrap and shrink, so it can never sit over the portal name.
 * Consumers pass their controls as a fragment: the header owns this layout, and
 * a consumer's own non-wrapping row would defeat it (AUTH-1).
 */
export function PortalHeader({ portalName, toolName, rightSlot, subNav }: PortalHeaderProps) {
  return (
    <header
      style={{
        background: 'var(--color-strip, var(--color-structural, var(--brand)))',
        borderBottom: '3px solid rgba(0, 0, 0, 0.18)',
      }}
    >
      <div
        data-portal-header-row=""
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 16,
          rowGap: 12,
          padding: '16px clamp(16px, 5vw, 28px)',
        }}
      >
        {/* Brand group — the icon and the portal identity travel together, so a
            wrap never strands the icon on a line of its own. `minWidth: 0` lets it
            shrink below its content so the name wraps rather than overflows. */}
        <div
          data-portal-header-brand=""
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          {/* CCA icon mark, inverted to white — locally bundled, not CDN-dependent.
              Links out to the public CCAANZ site in a new tab (org standard). */}
          <a
            href={CCA_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            title="Confessing Anglicans Aotearoa New Zealand — opens in a new tab"
            style={{ display: 'flex', flexShrink: 0, lineHeight: 0 }}
          >
            <img
              src={iconLogo}
              alt="Church of Confessing Anglicans Aotearoa New Zealand"
              style={{ width: 55, height: 55, flexShrink: 0, filter: 'brightness(0) invert(1)' }}
            />
          </a>

          {/* Portal identity — a long name breaks onto further lines, never clips. */}
          <div
            data-portal-header-identity=""
            style={{
              borderLeft:
                '1px solid color-mix(in srgb, var(--color-on-strip, #fff) 30%, transparent)',
              paddingLeft: 16,
              minWidth: 0,
              flex: 1,
              overflowWrap: 'break-word',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-xs, 0.72rem)',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--color-on-strip, #fff)',
                marginBottom: 1,
              }}
            >
              CCAANZ
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-base, 0.95rem)',
                fontWeight: 600,
                color: 'var(--color-on-strip, #fff)',
              }}
            >
              {portalName}
              {/* The tool name INHERITS the portal name's solid white; weight 400 is
                  its lower emphasis. It wore 75% white until cca-5weu — about 3.3:1
                  on the lightest (gold) strip, under AA's 4.5:1 (AUTH-1). */}
              {toolName && (
                <span style={{ fontWeight: 400, color: 'inherit' }}>
                  {/* A plain space BEFORE the dot (a line may break there) and a
                      non-breaking one after it, so a wrap never strands the dot
                      at the end of a line. */}{' '}
                  {'· '}
                  {toolName}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Right slot — user controls. Deliberately NOT `flexShrink: 0`: a slot
            that refuses to shrink is what laid the Settings pill over the portal
            name at 390px. It wraps internally and stays right-aligned. */}
        {rightSlot && (
          <div
            data-portal-header-slot=""
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              alignItems: 'center',
              // 8px between controls on a line; 12px between lines, the same
              // vertical rhythm as the brand-to-controls wrap (the row's rowGap).
              columnGap: 8,
              rowGap: 12,
              flex: '0 1 auto',
              marginInlineStart: 'auto',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {rightSlot}
          </div>
        )}
      </div>

      {/* Optional sub-navigation strip */}
      {subNav}
    </header>
  );
}
